"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { EllipsisVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { dashboardNavItems } from "@/lib/constants";
import type { RoleType } from "@/types";
import NavLinkContent from "@/components/shared/nav-link-content";

export default function MobileSheet({
  children,
  role,
}: {
  children: React.ReactNode;
  role: RoleType;
}) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);

  // Mirror the desktop sidebar exactly: ADMIN owns no venue → Vendors panel +
  // Account only; STAFF → Scanner (+ Account); VENDOR → all except admin-only.
  const visibleItems =
    role === "ADMIN"
      ? dashboardNavItems.filter(
          (i) => i.adminOnly || i.href === "/dashboard/account",
        )
      : role === "STAFF"
        ? dashboardNavItems.filter((i) => !i.vendorOnly && !i.adminOnly)
        : role === "VENDOR"
          ? dashboardNavItems.filter((i) => !i.adminOnly)
          : [];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger className="align-middle">
        <EllipsisVertical />
      </SheetTrigger>
      <SheetContent className="flex flex-col items-start gap-4 p-4">
        <SheetTitle>Menu</SheetTitle>
        <SheetDescription />

        <div className="flex flex-col space-y-10 h-full">
          <div className="flex flex-col gap-1 w-full">
            {visibleItems.map(({ href, label, icon: Icon }) => (
              <Link
                key={href}
                href={href}
                onClick={() => setOpen(false)}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  pathname === href
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <NavLinkContent Icon={Icon} label={label} />
              </Link>
            ))}
          </div>

          {/* Server-rendered children (UserCard) passed from parent */}
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
